import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { User } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import Logo from '../ui/Logo';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

// --- SVG icon components ---

function DashboardIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}

function BuildingIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  );
}

function DocumentIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function HomeIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function ChatIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  );
}

function CalendarIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  );
}

function LinkIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-3.03a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.343 8.28" />
    </svg>
  );
}

function UsersIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function ContractIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function CreditCardIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  );
}

function BanknotesIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  );
}

function CogIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function HeartIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  );
}

function AnalyticsIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  );
}

function WrenchIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
    </svg>
  );
}

function PaintBrushIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
    </svg>
  );
}

function getNavItems(role: string, t: (key: string, options?: Record<string, string>) => string): NavItem[] {
  const items: NavItem[] = [
    { label: t('nav.dashboard'), path: '/app', icon: <DashboardIcon /> },
  ];

  if (role === 'root') {
    items.push({ label: t('nav.companies'), path: '/app/admin/companies', icon: <BuildingIcon /> });
    items.push({ label: t('nav.siteContent'), path: '/app/admin/site-content', icon: <DocumentIcon /> });
    items.push({ label: t('nav.transfers', { defaultValue: 'Transferencias' }), path: '/app/admin/transfers', icon: <BanknotesIcon /> });
    items.push({ label: 'Usuarios', path: '/app/admin/users', icon: <UsersIcon /> });
    items.push({ label: 'Planes', path: '/app/admin/plans', icon: <CreditCardIcon /> });
    items.push({ label: 'Configuracion', path: '/app/admin/settings', icon: <CogIcon /> });
  }

  if (role === 'company_admin') {
    items.push({ label: t('nav.properties'), path: '/app/properties', icon: <HomeIcon /> });
    items.push({ label: t('nav.contracts', { defaultValue: 'Contratos' }), path: '/app/contracts/dashboard', icon: <ContractIcon /> });
    items.push({ label: t('nav.maintenance', { defaultValue: 'Mantenimiento' }), path: '/app/maintenance', icon: <WrenchIcon /> });
    items.push({ label: t('nav.messages'), path: '/app/messages', icon: <ChatIcon /> });
    items.push({ label: t('nav.visits', { defaultValue: 'Visitas' }), path: '/app/visits', icon: <CalendarIcon /> });
    items.push({ label: t('nav.analytics', { defaultValue: 'Analiticas' }), path: '/app/analytics', icon: <AnalyticsIcon /> });
    items.push({ label: t('nav.billing', { defaultValue: 'Facturacion' }), path: '/app/billing', icon: <CreditCardIcon /> });
    items.push({ label: t('nav.connections', { defaultValue: 'Conexiones' }), path: '/app/settings/connections', icon: <LinkIcon /> });
    items.push({ label: 'Usuarios', path: '/app/admin/users', icon: <UsersIcon /> });
    items.push({ label: t('nav.branding', { defaultValue: 'Personalizacion' }), path: '/app/settings/branding', icon: <PaintBrushIcon /> });
    items.push({ label: t('nav.settings'), path: '/app/settings', icon: <CogIcon /> });
  }

  if (role === 'agent') {
    items.push({ label: t('nav.myProperties'), path: '/app/properties', icon: <HomeIcon /> });
    items.push({ label: t('nav.messages'), path: '/app/messages', icon: <ChatIcon /> });
    items.push({ label: t('nav.visits', { defaultValue: 'Visitas' }), path: '/app/visits', icon: <CalendarIcon /> });
  }

  if (role === 'end_user') {
    items.push({ label: t('nav.favorites'), path: '/app/favorites', icon: <HeartIcon /> });
    items.push({ label: t('nav.myContracts', { defaultValue: 'Mis Contratos' }), path: '/app/contracts', icon: <ContractIcon /> });
    items.push({ label: t('nav.myVisits', { defaultValue: 'Mis Visitas' }), path: '/app/visits', icon: <CalendarIcon /> });
  }

  return items;
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

interface SidebarProps {
  user: User | null;
}

export default function Sidebar({ user }: SidebarProps): React.ReactElement {
  const { t } = useTranslation('common');
  const { sidebarMode, mobileSidebarOpen, setMobileSidebarOpen, cycleSidebar } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const navItems = getNavItems(user?.role || 'end_user', t);

  const handleNavClick = (path: string): void => {
    navigate(path);
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const isActive = (path: string): boolean => {
    if (path === '/app') return location.pathname === '/app';
    // Exact match for settings to avoid /settings matching /settings/connections
    if (path === '/app/settings') return location.pathname === '/app/settings';
    return location.pathname.startsWith(path);
  };

  // On mobile: always use overlay drawer mode
  // On desktop: use sidebarMode (open / mini / hidden)
  const isMini = !isMobile && sidebarMode === 'mini';
  const isHidden = !isMobile && sidebarMode === 'hidden';
  const showLabels = isMobile || sidebarMode === 'open';

  // Calculate sidebar pixel width
  const sidebarWidth = isMobile
    ? 240
    : sidebarMode === 'open' ? 240 : sidebarMode === 'mini' ? 64 : 0;

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobile && mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar container */}
      <aside
        className={`
          bg-neutral text-neutral-content flex flex-col overflow-hidden
          transition-all duration-300 ease-in-out
          ${isMobile
            ? `fixed top-0 left-0 h-full z-50 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
            : 'relative z-30 h-full flex-shrink-0'
          }
        `}
        style={{
          width: isMobile ? 240 : sidebarWidth,
          minWidth: isMobile ? 240 : sidebarWidth,
        }}
      >
        {/* Brand header */}
        <div className={`flex items-center min-h-[64px] border-b border-neutral-content/10 ${isMini ? 'justify-center px-2' : 'px-4'}`}>
          {!isMini && (
            <Logo size={32} showText textClassName="text-neutral-content" />
          )}
          {isMini && (
            <Logo size={28} />
          )}
        </div>

        {/* Navigation items */}
        <nav className="flex-1 overflow-y-auto py-3">
          <ul className={`flex flex-col gap-0.5 ${isMini ? 'items-center px-1.5' : 'px-2'}`}>
            {navItems.map((item) => {
              const active = isActive(item.path);

              if (isMini) {
                return (
                  <li key={item.path}>
                    <div className="tooltip tooltip-right z-[60]" data-tip={item.label}>
                      <button
                        className={`
                          w-10 h-10 flex items-center justify-center rounded-lg
                          transition-colors duration-150
                          ${active
                            ? 'bg-primary text-primary-content'
                            : 'text-neutral-content/70 hover:bg-neutral-content/10 hover:text-neutral-content'
                          }
                        `}
                        onClick={() => handleNavClick(item.path)}
                      >
                        {item.icon}
                      </button>
                    </div>
                  </li>
                );
              }

              return (
                <li key={item.path}>
                  <button
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                      text-sm font-medium transition-colors duration-150 whitespace-nowrap overflow-hidden
                      ${active
                        ? 'bg-primary text-primary-content'
                        : 'text-neutral-content/70 hover:bg-neutral-content/10 hover:text-neutral-content'
                      }
                    `}
                    onClick={() => handleNavClick(item.path)}
                  >
                    {active && <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary-content/30 rounded-r" />}
                    <span className="flex-shrink-0">{item.icon}</span>
                    {showLabels && <span className="truncate">{item.label}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom collapse/expand button (desktop only) */}
        {!isMobile && !isHidden && (
          <div className={`border-t border-neutral-content/10 ${isMini ? 'flex justify-center' : 'flex justify-end'} p-2`}>
            <button
              onClick={cycleSidebar}
              className="btn btn-ghost btn-sm btn-square text-neutral-content/60 hover:text-neutral-content hover:bg-neutral-content/10"
              aria-label="Toggle sidebar"
            >
              {sidebarMode === 'open' ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              )}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
